/**
 * Module #34 Phase A chunk 2 — conversation history sidebar.
 *
 * Rendered as the left rail of /admin/copilot. Lists the signed-in user's
 * own conversations (most recent first), provides a "New chat" button at
 * the top, click-to-load, hover-to-archive.
 */

import { useRef, useState } from 'react';
import type {
  CopilotConversationListItem,
  UseCopilotConversationsReturn,
} from '../../hooks/useCopilotConversations';

export interface ConversationSidebarProps {
  list: UseCopilotConversationsReturn;
  activeConversationId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
}

export function ConversationSidebar({
  list,
  activeConversationId,
  onSelect,
  onNewChat,
}: ConversationSidebarProps) {
  return (
    <aside
      style={{
        width: '16rem',
        borderRight: '1px solid var(--border)',
        background: 'var(--surface-elev)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '0.75rem',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <button
          type="button"
          onClick={onNewChat}
          style={{
            width: '100%',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--border)',
            borderRadius: '0.375rem',
            background: 'var(--surface)',
            color: 'var(--text)',
            cursor: 'pointer',
            fontSize: '0.8125rem',
            fontWeight: 600,
            textAlign: 'left',
          }}
        >
          + New chat
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {list.status.kind === 'loading' && (
          <div style={{ padding: '1rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            Loading…
          </div>
        )}
        {list.status.kind === 'error' && (
          <div
            style={{ padding: '1rem', fontSize: '0.75rem', color: 'var(--danger-text, #991b1b)' }}
          >
            {list.status.detail}
          </div>
        )}
        {list.status.kind === 'idle' && list.conversations.length === 0 && (
          <div style={{ padding: '1rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
            No conversations yet. Start one to see it here.
          </div>
        )}
        {list.conversations.map((c) => (
          <ConversationRow
            key={c.conversationId}
            item={c}
            active={c.conversationId === activeConversationId}
            onSelect={onSelect}
            onArchive={list.archive}
            onRename={list.rename}
          />
        ))}
      </div>
    </aside>
  );
}

interface ConversationRowProps {
  item: CopilotConversationListItem;
  active: boolean;
  onSelect: (id: string) => void;
  onArchive: (id: string) => Promise<boolean>;
  onRename: (id: string, title: string) => Promise<boolean>;
}

function ConversationRow({ item, active, onSelect, onArchive, onRename }: ConversationRowProps) {
  const displayTitle = item.title ?? defaultTitle(item);
  const subtitle = formatRelative(item.lastMessageAt ?? item.createdAt);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // One-shot guard: Enter, blur, and Escape can all fire around the same
  // unmount (e.g. Escape → setEditing(false) → input unmounts → blur). The
  // FIRST of them wins; later ones are no-ops, so we never double-save or
  // fire a duplicate rename request.
  const committedRef = useRef(false);

  const finish = (save: boolean): void => {
    if (committedRef.current) return;
    committedRef.current = true;
    setEditing(false);
    if (!save) return;
    const value = draft.replace(/\s+/g, ' ').trim();
    if (value.length > 0 && value !== displayTitle) {
      void onRename(item.conversationId, value);
    }
  };

  const iconButton = {
    position: 'absolute' as const,
    top: '0.375rem',
    width: '1.25rem',
    height: '1.25rem',
    padding: 0,
    border: 'none',
    background: 'transparent',
    color: 'inherit',
    opacity: 0.4,
    cursor: 'pointer',
    fontSize: '0.75rem',
    borderRadius: '0.25rem',
  };

  return (
    <div
      style={{
        position: 'relative',
        padding: '0.625rem 0.75rem',
        borderBottom: '1px solid var(--border)',
        background: active ? 'var(--system-brand-accent)' : 'transparent',
        color: active ? 'var(--system-brand-accent-fg)' : 'var(--text)',
        cursor: editing ? 'default' : 'pointer',
      }}
      onClick={() => {
        if (!editing) onSelect(item.conversationId);
      }}
    >
      {editing ? (
        <input
          autoFocus
          value={draft}
          maxLength={80}
          aria-label="Conversation title"
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              finish(true);
            } else if (e.key === 'Escape') {
              e.preventDefault();
              finish(false);
            }
          }}
          onBlur={() => finish(true)}
          style={{
            width: '100%',
            boxSizing: 'border-box',
            fontSize: '0.8125rem',
            fontWeight: 600,
            marginBottom: '0.125rem',
            padding: '0.0625rem 0.25rem',
            border: '1px solid var(--border)',
            borderRadius: '0.25rem',
            background: 'var(--surface)',
            color: 'var(--text)',
          }}
        />
      ) : (
        <div
          style={{
            fontSize: '0.8125rem',
            fontWeight: 600,
            marginBottom: '0.125rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingRight: '2.75rem',
          }}
        >
          {displayTitle}
        </div>
      )}
      <div
        style={{
          fontSize: '0.6875rem',
          color: active ? 'rgba(255,255,255,0.8)' : 'var(--text-muted)',
          display: 'flex',
          justifyContent: 'space-between',
          gap: '0.5rem',
        }}
      >
        <span>{subtitle}</span>
        <span>{item.messageCount} msg</span>
      </div>
      {!editing && (
        <>
          <button
            type="button"
            aria-label="Rename conversation"
            title="Rename"
            onClick={(e) => {
              e.stopPropagation();
              committedRef.current = false;
              setDraft(item.title ?? '');
              setEditing(true);
            }}
            style={{ ...iconButton, right: '1.75rem' }}
          >
            ✎
          </button>
          <button
            type="button"
            aria-label="Archive conversation"
            title="Archive"
            onClick={(e) => {
              e.stopPropagation();
              void onArchive(item.conversationId);
            }}
            style={{ ...iconButton, right: '0.375rem' }}
          >
            ✕
          </button>
        </>
      )}
    </div>
  );
}

function defaultTitle(item: CopilotConversationListItem): string {
  // Fallback only — the server auto-titles new conversations from their
  // first exchange, and backfills legacy ones. This UUID-prefix label shows
  // for a chat whose title hasn't been generated yet (or generation failed).
  return `Chat ${item.conversationId.slice(0, 8)}`;
}

function formatRelative(iso: string | null): string {
  if (iso === null) return '';
  const date = new Date(iso);
  const now = Date.now();
  const diffMs = now - date.getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}
